/**
 *  @class
 *  @function Pagination - Scroll infinito para blog y colecciones
 */
if (!customElements.get('pagination-theme')) {
  // 1x1 transparent gif: placeholder `src` for images handed off to lazysizes below, so the
  // browser has nothing to fetch until lazysizes swaps in the real `data-srcset` near the
  // viewport (same idea as the blank/no-op `src` lazysizes already tolerates elsewhere).
  const LAZY_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

  // Restoring scroll position after a product visit means re-fetching tandas the user
  // already saw. Measured against production (curl, Aug 2026): a full-page fetch (the
  // request `loadProducts()` was already making) weighs ~2.8MB per tanda of 36-48
  // products; the same tanda via `?section_id=` (Shopify's partial-render endpoint) is
  // ~0.7MB — about 4x lighter since it skips head/header/footer/other sections. Even at
  // that reduced weight, re-fetching an unbounded number of tandas on return is still a
  // real cost on iOS cellular, so auto-restore is capped and anything deeper requires an
  // explicit tap (see restoreScrollState/showContinueAffordance below).
  const RESTORE_AUTO_CAP = 3; // tandas restored silently (~2MB worst case, 36-48/tanda)
  const RESTORE_TTL_MS = 30 * 60 * 1000; // stock/order can drift if the tab sits longer
  const STORAGE_PREFIX = 'ng_infinite_scroll:';

  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      
      // Configuración general
      this.contentType = this.dataset.contentType || 'generic';
      this.currentPage = parseInt(this.dataset.currentPage) || 1;
      this.totalPages = parseInt(this.dataset.totalPages) || 1;
      
      // Buscar el contenedor de items
      // Primero intentar con el ID de la sección, luego con selectores genéricos
      this.grid = document.querySelector('[data-id="' + id + '"]') ||
                  document.querySelector('#product-grid') ||
                  document.querySelector('.blog-section .row') ||
                  document.querySelector('ul.products.collection') ||
                  document.querySelector('.products.collection');
      
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.i = this.currentPage + 1; // Siguiente página a cargar
      this.restoring = false; // true while replaying previously-seen tandas after a back-navigation
      
      // Debug info
      // console.log('Pagination initialized:', {
      //   id: id,
      //   contentType: this.contentType,
      //   currentPage: this.currentPage,
      //   totalPages: this.totalPages,
      //   nextPage: this.i,
      //   button: this.button,
      //   grid: this.grid,
      //   hasMore: this.currentPage < this.totalPages
      // });
    }
    
    connectedCallback() {
      // Si no hay más páginas, mostrar mensaje y salir
      if (this.currentPage >= this.totalPages) {
        //console.log('No more pages available');
        this.endPagination();
        return;
      }
      
      // Remover la clase visually-hidden si hay más páginas
      if (this.button && this.button.classList.contains('visually-hidden')) {
        this.button.classList.remove('visually-hidden');
      }
      
      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        // Page 1 is server-rendered, not appended through renderProducts(), so it never
        // went through observeBatchForRecycling() below — its images (now mostly lazy
        // too, see snippets/product-card.liquid eager_load) sat unrecycled forever,
        // keeping a permanent baseline of ~products_per_page decoded images in memory
        // before any infinite-scroll page even loaded. Register it the same way.
        if (this.grid) {
          const initialItems = Array.from(this.grid.children).filter((child) => child.tagName === 'LI');
          this.observeBatchForRecycling(initialItems);
        }
        // Replay tandas the user had already loaded before leaving (product page visit,
        // back button) before arming the live observer — Shopify serves collection pages
        // with Cache-Control: private, no-store, so Safari/WebKit never bfcaches them and
        // "atrás" always lands back on a fresh page 1 otherwise.
        this.restoreScrollState().finally(() => {
          // Si la restauración ya agotó las páginas disponibles, endPagination() se
          // encargó de todo — no re-armar el observer para que vuelva a "terminar" en
          // cuanto el sentinela entre en viewport.
          if (this.i <= this.totalPages) {
            this.infinite();
          }
        });
      }
    }
    
    addUrlParam(search, key) {
      var newParam = key + '=' + this.i,
        params = '?' + newParam;
      
      if (search) {
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);
        if (params === search) {
          params += '&' + newParam;
        }
      }
      return params;
    }
    
    loadMore() {
      let base = this;
      this.button.addEventListener('click', function(e) {
        e.preventDefault();
        base.loadProducts();
        this.blur();
        return false;
      });
    }
    
    infinite() {
      let base = this;
      
      // Crear un elemento sentinela al final del grid para detectar cuando está visible
      if (!base.sentinel) {
        base.sentinel = document.createElement('div');
        base.sentinel.className = 'pagination-sentinel';
        base.sentinel.style.height = '1px';
        base.sentinel.style.width = '100%';
        base.sentinel.style.visibility = 'hidden';
        base.sentinel.style.pointerEvents = 'none';
        
        // Colocar el sentinela justo antes del elemento de paginación
        if (base.grid && base.grid.parentNode) {
          base.grid.parentNode.insertBefore(base.sentinel, base);
        } else {
          base.insertAdjacentElement('beforebegin', base.sentinel);
        }
      }
      
      base.observer = new IntersectionObserver(function(entries) {
        entries.forEach(entry => {
          // Cuando el sentinela o el elemento de paginación es visible, cargar más productos
          if (entry.isIntersecting && entry.intersectionRatio > 0) {
            // Verificar que no estemos ya cargando y que haya más páginas
            if (!base.getAttribute('loading') && base.i <= base.totalPages) {
              base.loadProducts();
            }
          }
        });
      }, {
        root: null,
        rootMargin: '200px', // Cargar cuando esté a 200px de ser visible (más anticipación)
        threshold: [0, 0.1, 0.5, 1]
      });
      
      // Observar el elemento de paginación y el sentinela
      base.observer.observe(base);
      if (base.sentinel) {
        base.observer.observe(base.sentinel);
      }

      // Mantener sessionStorage al día con la posición de scroll aunque el usuario no
      // dispare ninguna carga nueva (p.ej. ya está en la última tanda cargada). Sin esto
      // solo se guardaría scrollY en el momento exacto de cargar una tanda, que puede
      // quedar desactualizado si el usuario sigue bajando después. beforeunload/pagehide
      // no se usan aquí porque no son fiables en iOS Safari — se prefiere ir guardando el
      // estado sobre la marcha.
      if (!base._scrollSaveBound) {
        base._scrollSaveBound = true;
        let scrollSaveTimer = null;
        window.addEventListener('scroll', () => {
          if (scrollSaveTimer) return;
          scrollSaveTimer = setTimeout(() => {
            scrollSaveTimer = null;
            base.saveScrollState();
          }, 500);
        }, { passive: true });
      }
    }
    
    loadProducts() {
      let base = this,
        url = document.location.pathname + base.addUrlParam(document.location.search, 'page');

      // `?section_id=` asks Shopify to render just this section instead of the full
      // document (header, footer, other sections, all their assets) — measured ~4x
      // lighter per tanda in production (~0.7MB vs ~2.8MB for a 36-48 product tanda).
      // Same DOM structure comes back (#product-grid, li.column/colx), so renderProducts()
      // below needs no changes to parse it.
      if (this.dataset.section) {
        url += (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(this.dataset.section);
      }

      if (base.getAttribute('loading')) {
        //console.log('Already loading, skipping...');
        return Promise.resolve();
      }

      // Verificar si hay más páginas
      if (this.i > this.totalPages) {
        //console.log('No more pages to load');
        this.endPagination();
        return Promise.resolve();
      }

      //console.log('Loading page:', this.i, 'of', this.totalPages, 'URL:', url);

      base.setAttribute('loading', 'true');

      return fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((responseText) => {
          //console.log('Response received, parsing...');
          base.renderProducts(responseText, url);
          dispatchCustomEvent('pagination:page-changed', {
            url: url,
            page: base.i,
            type: base.contentType
          });
          // Incrementar para la siguiente carga después de renderizar
          base.i++;
          base.saveScrollState();
        })
        .catch((error) => {
          console.error('Error loading more content:', error);
          base.removeAttribute('loading');
          base.showError();
        });
    }
    
    renderProducts(html, url) {
      let base = this;
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      let items = [];
      if (this.contentType === 'blog') {
        const blogSection = doc.querySelector('.blog-section');
        if (!blogSection) {
          //console.log('No blog section found in response');
          this.endPagination();
          return;
        }
        const grid_to_replace = blogSection.querySelector('.row');
        if (!grid_to_replace) {
          //console.log('No .row found in blog section');
          this.endPagination();
          return;
        }
        items = grid_to_replace.querySelectorAll('.columns');
      } else {
        // Para productos, buscar el grid de varias formas posibles
        const productGrid = doc.querySelector('#product-grid') || 
                           doc.querySelector('[id^="product-grid"]') ||
                           doc.querySelector('ul.products.collection') ||
                           doc.querySelector('.products.collection');
        
        if (!productGrid) {
          //console.log('No product grid found in response');
          this.endPagination();
          return;
        }
        
        // Obtener los elementos li dentro del grid
        items = productGrid.querySelectorAll('li.column, li.colx');
        
        // Si no encuentra con querySelectorAll, intentar con children
        if (items.length === 0) {
          const children = Array.from(productGrid.children);
          items = children.filter(child => child.tagName === 'LI');
        }
      }
      
      // Convertir NodeList/HTMLCollection a Array para mejor manejo
      items = Array.from(items);
      
      //console.log('Found items in response:', items.length);
      //console.log('Items HTML sample:', items.length > 0 ? items[0].outerHTML.substring(0, 200) : 'No items');

      if (items.length === 0) {
        //console.log('No items found, ending pagination');
        this.endPagination();
        return;
      }

      // Verificar que el grid existe en el DOM actual
      if (!this.grid) {
        console.error('Product grid not found in current DOM');
        this.endPagination();
        return;
      }

      // Clonar y añadir cada elemento
      const addedItems = [];
      for (let i = 0; i < items.length; i++) {
        const clonedPost = items[i].cloneNode(true);
        // The primary product-card image is rendered with `loading="eager"
        // fetchpriority="high"` (snippets/product-card.liquid) for LCP on the initially
        // visible products — it's a plain <img src/srcset>, not lazysizes' `.lazyload` +
        // data-srcset pattern, so lazySizes.loader.checkElems() below has no effect on it.
        // Left as-is, the browser starts downloading every one of these full-size images
        // the instant appendChild() below inserts it into the DOM, regardless of scroll
        // position — a burst of N eager fetches per infinite-scroll page. Hand it off to
        // lazysizes instead (same mechanism already used for the secondary carousel
        // images) rather than relying on native `loading="lazy"` for a dynamically
        // inserted node, whose deferral behavior isn't guaranteed across WebKit/iOS
        // versions.
        clonedPost.querySelectorAll('img[loading="eager"]').forEach((img) => {
          const srcset = img.getAttribute('srcset');
          if (srcset) {
            img.setAttribute('data-srcset', srcset);
            img.removeAttribute('srcset');
          }
          if (img.hasAttribute('sizes')) {
            img.setAttribute('data-sizes', 'auto');
            img.removeAttribute('sizes');
          }
          img.removeAttribute('loading');
          img.removeAttribute('fetchpriority');
          img.setAttribute('src', LAZY_PLACEHOLDER);
          img.classList.add('lazyload');
        });
        this.grid.appendChild(clonedPost);
        addedItems.push(clonedPost);
        //console.log('Added item:', i + 1, clonedPost.querySelector('h5') ? clonedPost.querySelector('h5').textContent.trim() : '');
      }
      
      // Mover el sentinela justo antes del elemento de paginación después de agregar items
      if (this.sentinel) {
        // Remover el sentinela de su posición actual si existe
        if (this.sentinel.parentNode) {
          this.sentinel.parentNode.removeChild(this.sentinel);
        }
        // Reinsertar el sentinela justo antes del elemento de paginación
        if (this.grid && this.grid.parentNode) {
          this.grid.parentNode.insertBefore(this.sentinel, this);
        } else {
          this.insertAdjacentElement('beforebegin', this.sentinel);
        }
      }
      
      // Reinicializar lazy loading
      this.initializeLazyLoading(addedItems);

      // Vigilar este lote para liberar sus imágenes cuando quede muy por encima del
      // viewport (ver observeBatchForRecycling) — sin esto, ninguna imagen decodificada se
      // libera nunca mientras dure el scroll infinito, y la memoria crece sin límite.
      this.observeBatchForRecycling(addedItems);

      // Animaciones — se omiten al restaurar tandas ya vistas (this.restoring): con 36-48
      // productos por tanda, el stagger de 0.05-0.1s por item añadiría hasta varios
      // segundos de espera visible a algo que debería sentirse instantáneo.
      if (this.restoring) {
        addedItems.forEach((item) => {
          item.style.opacity = '1';
          item.style.transform = 'translateY(0)';
        });
        base.removeAttribute('loading');
        base.checkIfEndReached();
      } else if (this.animations_enabled && typeof gsap !== 'undefined') {
        gsap.set(addedItems, {
          opacity: 0,
          y: 30
        });
        gsap.to(addedItems, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: function() {
            base.removeAttribute('loading');
            // Verificar si hemos llegado al final después de la animación
            base.checkIfEndReached();
          }
        });
      } else {
        // Animación CSS fallback
        addedItems.forEach((item, index) => {
          setTimeout(() => {
            item.style.opacity = '1';
            item.style.transform = 'translateY(0)';
          }, index * 100);
        });
        base.removeAttribute('loading');
        // Verificar si hemos llegado al final
        base.checkIfEndReached();
      }
    }
    
    checkIfEndReached() {
      // Verificar si hemos llegado al final
      // this.i ya fue incrementado en loadProducts, así que comparamos con totalPages
      if (this.i > this.totalPages) {
        //console.log('Reached end of pages');
        this.endPagination();
      }
    }

    getStorageKey() {
      return STORAGE_PREFIX + document.location.pathname + document.location.search;
    }

    saveScrollState() {
      // this.i ya apunta a la siguiente página a cargar; la última realmente renderizada
      // es this.i - 1. No merece la pena persistir si el usuario sigue en la página 1.
      const lastLoadedPage = this.i - 1;
      if (lastLoadedPage <= this.currentPage) {
        return;
      }
      try {
        sessionStorage.setItem(this.getStorageKey(), JSON.stringify({
          page: lastLoadedPage,
          scrollY: window.scrollY,
          savedAt: Date.now()
        }));
      } catch (e) {
        // Safari en modo privado, cuota llena, etc. — la restauración es "best effort".
      }
    }

    // Reconstruye, en secuencia (nunca en paralelo, para no lanzar varios fetch de ~0.7MB
    // a la vez en una red móvil), las tandas que el usuario ya había cargado antes de salir
    // a una ficha de producto. Va hasta RESTORE_AUTO_CAP tandas en automático; más allá de
    // eso pide confirmación explícita (showContinueAffordance) en vez de arrastrar de golpe
    // un historial de scroll largo.
    async restoreScrollState() {
      let saved;
      try {
        const raw = sessionStorage.getItem(this.getStorageKey());
        if (!raw) {
          return;
        }
        saved = JSON.parse(raw);
      } catch (e) {
        return;
      }

      if (!saved || !saved.page || saved.page <= this.currentPage) {
        return;
      }
      if (!saved.savedAt || Date.now() - saved.savedAt > RESTORE_TTL_MS) {
        try { sessionStorage.removeItem(this.getStorageKey()); } catch (e) {}
        return;
      }

      const target = Math.min(saved.page, this.totalPages);
      const autoTarget = Math.min(target, this.currentPage + RESTORE_AUTO_CAP);

      this.restoring = true;
      await this.restoreUpTo(autoTarget);
      this.restoring = false;

      if (autoTarget >= target) {
        // Salto instantáneo, sin animación de scroll: es una restauración, no una
        // navegación nueva que el usuario deba percibir.
        window.scrollTo(0, saved.scrollY);
      } else {
        this.showContinueAffordance(target, saved.scrollY);
      }
    }

    async restoreUpTo(targetPage) {
      while (this.i <= targetPage && this.i <= this.totalPages) {
        await this.loadProducts();
      }
    }

    showContinueAffordance(targetPage, scrollY) {
      const base = this;
      const banner = document.createElement('div');
      banner.className = 'pagination-restore-banner';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button';
      button.textContent = 'Continuar donde lo dejaste';
      banner.appendChild(button);

      button.addEventListener('click', async () => {
        button.disabled = true;
        button.classList.add('loading');
        base.restoring = true;
        await base.restoreUpTo(targetPage);
        base.restoring = false;
        banner.remove();
        window.scrollTo({ top: scrollY, behavior: 'smooth' });
      });

      if (this.grid && this.grid.parentNode) {
        this.grid.parentNode.insertBefore(banner, this.grid);
      } else {
        this.insertAdjacentElement('beforebegin', banner);
      }
    }

    initializeLazyLoading(newPosts) {
      // lazysizes (assets/vendor.min.js) watches the whole document for `.lazyload`
      // elements via its own MutationObserver and loads each one only as it nears the
      // viewport — appended items are picked up automatically, no manual trigger needed.
      // This used to force `img.srcset = img.dataset.srcset` / `img.src = img.dataset.src`
      // on every appended image immediately, which defeated that deferral: a single
      // infinite-scroll page could eagerly download dozens of full-size images the
      // instant it was appended, regardless of scroll position. That eager image load is
      // what was ballooning memory on iOS and causing Safari/WebKit to kill and reload
      // the tab. checkElems() just asks lazysizes to re-scan now instead of waiting for
      // its next throttled check — it still only loads images that are actually near the
      // viewport.
      if (typeof window.lazySizes !== 'undefined') {
        window.lazySizes.loader.checkElems();
      }
    }
    
    // Reduce eager/lazy strategies solo evitan cargas de golpe, pero no liberan nada: en
    // scroll infinito continuo, cada imagen que llega a entrar en el viewport se queda
    // decodificada en memoria para siempre (nunca se recicla), así que tras varias tandas
    // (p.ej. products_per_page: 36 × hasta 4 imágenes) el total sigue creciendo sin límite
    // hasta que iOS mata la pestaña por presión de memoria. Para evitarlo, cada lote de
    // productos añadido se vigila con un IntersectionObserver compartido usando su primer
    // <li> como marcador: en cuanto ese marcador queda completamente por encima del
    // viewport (el usuario ya hizo scroll más allá de todo el lote), se liberan sus
    // imágenes. Si el usuario vuelve a subir, lazysizes las vuelve a cargar solo (su propio
    // listener de scroll/resize detecta los elementos `.lazyload` de nuevo).
    observeBatchForRecycling(items) {
      if (!items.length) {
        return;
      }
      if (!this.batchObserver) {
        this.batchMarkers = new Map();
        this.batchObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const batchItems = this.batchMarkers.get(entry.target);
            if (!batchItems) {
              return;
            }
            if (entry.isIntersecting) {
              if (typeof window.lazySizes !== 'undefined') {
                window.lazySizes.loader.checkElems();
              }
            } else if (entry.boundingClientRect.top < 0) {
              this.unloadBatchImages(batchItems);
            }
          });
        }, {
          threshold: 0
        });
      }
      const marker = items[0];
      this.batchMarkers.set(marker, items);
      this.batchObserver.observe(marker);
    }

    unloadBatchImages(items) {
      items.forEach((item) => {
        item.querySelectorAll('img.lazyload, img.lazyloaded').forEach((img) => {
          if (!img.dataset.srcset && !img.dataset.src) {
            return;
          }
          img.setAttribute('src', LAZY_PLACEHOLDER);
          img.removeAttribute('srcset');
          img.classList.remove('lazyloaded');
          img.classList.add('lazyload');
        });
      });
    }

    endPagination() {
      if (this.observer) {
        this.observer.unobserve(this);
        if (this.sentinel) {
          this.observer.unobserve(this.sentinel);
          if (this.sentinel.parentNode) {
            this.sentinel.parentNode.removeChild(this.sentinel);
          }
        }
        this.observer.disconnect();
      }
      this.removeAttribute('loading');
      if (this.button) {
        this.button.classList.add('visually-hidden');
      }
      // No queda nada que restaurar más allá de este punto.
      try { sessionStorage.removeItem(this.getStorageKey()); } catch (e) {}
      this.showEndMessage();
    }
    
    showEndMessage() {
      if (!this.querySelector('.pagination-end-message')) {
      const endMessage = document.createElement('div');
      endMessage.className = 'pagination-end-message';
      
      let message = 'No hay más artículos que mostrar';
      
      if (window.location.host === 'newgardenshopping.nl') {
        message = 'Er zijn geen items meer om weer te geven';
      } else if (window.location.host === 'newgardenshop.pt') {
        message = 'Não existem mais itens para apresentar';
      }
      
      const storeTexts = window.mktPaginationTexts;
      if (storeTexts && storeTexts.noMore) message = storeTexts.noMore;

      endMessage.innerHTML = `<p>${message}</p>`;
      this.appendChild(endMessage);
      }
    }
    
    showError() {
      if (!this.querySelector('.pagination-error-message')) {
        const errorMessage = document.createElement('div');
        errorMessage.className = 'pagination-error-message';
        const storeTexts = window.mktPaginationTexts || {};
        errorMessage.innerHTML = '<p>' + (storeTexts.error || 'Error al cargar más artículos.') + ' <button onclick="location.reload()">' + (storeTexts.retry || 'Intentar de nuevo') + '</button></p>';
        this.appendChild(errorMessage);
      }
    }
  }
  
  customElements.define('pagination-theme', Pagination);
}

// Función helper para eventos personalizados
function dispatchCustomEvent(eventName, detail = {}) {
  const event = new CustomEvent(eventName, {
    detail: detail,
    bubbles: true,
    cancelable: true
  });
  document.dispatchEvent(event);
}